import { driver } from 'driver.js'

// Master kill-switch for the driver.js onboarding tours. While false, all
// start*Tour() functions no-op and the "Replay tour" buttons are hidden (the
// components import this flag for their v-if). Flip to true to re-enable.
// See the "Re-enable or remove the driver.js onboarding tours" item in BACKLOG.md.
export const TOURS_ENABLED = false

const KEYS = {
    editorClose: 'wm_tour_editor_close_seen',
    editorDirty: 'wm_tour_editor_dirty_seen',
    reader: 'wm_tour_reader_seen',
}

function hasSeen(key) {
    return !!localStorage.getItem(key)
}

function markSeen(key) {
    localStorage.setItem(key, '1')
}

function clearSeen(key) {
    localStorage.removeItem(key)
}

function visibleSteps(steps) {
    return steps.filter(s => document.querySelector(s.element))
}

/** Starts the editor close tour if not yet seen. All data-tour target elements must be in the DOM at call time. */
export function startEditorCloseTour() {
    if (!TOURS_ENABLED) return
    if (hasSeen(KEYS.editorClose)) return
    const steps = visibleSteps([
        {
            element: '[data-tour="wm-btn-close"]',
            popover: {
                title: 'Close',
                description: 'No changes have been made, so you can safely close this page. Click Close to go back.',
                side: 'bottom',
                align: 'start',
            },
        },
    ])
    if (steps.length === 0) return
    const driverObj = driver({
        showProgress: false,
        steps,
        onDestroyStarted: () => {
            markSeen(KEYS.editorClose)
            // driver.js design: onDestroyStarted fires before teardown; calling destroy()
            // here passes s=false internally, skipping this callback and completing teardown.
            driverObj.destroy()
        },
    })
    driverObj.drive()
}

/** Starts the editor dirty-state tour if not yet seen. All data-tour target elements must be in the DOM at call time. */
export function startEditorDirtyTour() {
    if (!TOURS_ENABLED) return
    if (hasSeen(KEYS.editorDirty)) return
    const steps = visibleSteps([
        {
            element: '[data-tour="wm-btn-save"]',
            popover: {
                title: 'Save',
                description: 'Save your changes and continue editing.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="wm-btn-save-close"]',
            popover: {
                title: 'Save and Close',
                description: 'Save your changes and return to the previous page.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="wm-btn-cancel"]',
            popover: {
                title: 'Cancel',
                description:
                    'Discard your unsaved changes and go back. You will be asked to confirm before anything is lost.',
                side: 'bottom',
                align: 'start',
            },
        },
    ])
    if (steps.length === 0) return
    const driverObj = driver({
        showProgress: true,
        steps,
        onDestroyStarted: () => {
            markSeen(KEYS.editorDirty)
            // driver.js design: onDestroyStarted fires before teardown; calling destroy()
            // here passes s=false internally, skipping this callback and completing teardown.
            driverObj.destroy()
        },
    })
    driverObj.drive()
}

/** Starts the reader tour if not yet seen. All data-tour target elements must be in the DOM at call time. */
export function startReaderTour() {
    if (!TOURS_ENABLED) return
    if (hasSeen(KEYS.reader)) return
    const steps = visibleSteps([
        {
            element: '[data-tour="wm-btn-edit"]',
            popover: {
                title: 'Edit',
                description: 'Open this item in the editor to make changes.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="wm-btn-copy"]',
            popover: {
                title: 'Copy',
                description: 'Create a duplicate of this item as a starting point for a new one.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="wm-btn-archive"]',
            popover: {
                title: 'Archive',
                description:
                    "Move this item out of the active list. It won't be deleted — you can restore it from the archive if needed.",
                side: 'bottom',
                align: 'start',
            },
        },
    ])
    if (steps.length === 0) return
    const driverObj = driver({
        showProgress: true,
        steps,
        onDestroyStarted: () => {
            markSeen(KEYS.reader)
            // driver.js design: onDestroyStarted fires before teardown; calling destroy()
            // here passes s=false internally, skipping this callback and completing teardown.
            driverObj.destroy()
        },
    })
    driverObj.drive()
}

/** Clears seen-state for both editor tours so they will show again. */
export function resetEditorTours() {
    clearSeen(KEYS.editorClose)
    clearSeen(KEYS.editorDirty)
}

/** Clears seen-state for the reader tour so it will show again. */
export function resetReaderTour() {
    clearSeen(KEYS.reader)
}
